import {
  StepDirection,
  CardTypes,
  PaymentMethod,
  PaymentFrequency,
  YesNoAnswer,
} from 'scenes/common/types';
import {
  DefaultCoverType,
  MotorCoverType,
  MotorInsuranceType,
  MotorFormAnswer,
  MotorApiResponses,
} from 'scenes/motor/types';
import { CoverTypeQueryParam } from 'scenes/motor/flows/quoteDetails/types';
import {
  MotorFlowType,
  MotorMoveFlowAction,
  MotorMoveFlows,
  MotorStepType,
} from 'scenes/motor/flows/types';

export const mapDefaultCoverOption = (
  queryParam: string | undefined
): DefaultCoverType | undefined => {
  switch (queryParam) {
    case CoverTypeQueryParam.COMP:
      return {
        preselectedInsuranceType: MotorInsuranceType.Comprehensive,
        preselectedCoverType: MotorCoverType.Comprehensive,
      };
    case CoverTypeQueryParam.TPPD:
      return {
        preselectedInsuranceType: MotorInsuranceType.ThirdParty,
        preselectedCoverType: MotorCoverType.ThirdPartyPropertyDamage,
      };
    case CoverTypeQueryParam.TPFT:
      return {
        preselectedInsuranceType: MotorInsuranceType.ThirdParty,
        preselectedCoverType: MotorCoverType.ThirdPartyFireAndTheft,
      };
    default:
      return undefined;
  }
};

export const setDefaultCoverOption = (
  defaultedCoverType: DefaultCoverType
): MotorFormAnswer => ({
  direction: StepDirection.StayHere,
  formValues: { ...defaultedCoverType },
  apiResponses: {},
});

export const setPreselectedPaymentFrequency = (
  preselectedPaymentFrequency: PaymentFrequency
): MotorFormAnswer => ({
  direction: StepDirection.StayHere,
  formValues: { preselectedPaymentFrequency },
  apiResponses: {},
});

export const setPrefilledCampaignId = (
  prefilledCampaignId: string
): MotorFormAnswer => ({
  direction: StepDirection.StayHere,
  formValues: { prefilledCampaignId },
  apiResponses: {},
});

export const setPrefilledPromoCode = (
  prefilledPromoCode: string
): MotorFormAnswer => ({
  direction: StepDirection.StayHere,
  formValues: { prefilledPromoCode, promoCode: prefilledPromoCode },
  apiResponses: {},
});

export const setColesCampaignCode = (
  colesCampaignCode: string
): MotorFormAnswer => ({
  direction: StepDirection.StayHere,
  formValues: { colesCampaignCode },
  apiResponses: {},
});

export const setOptionalExtras = (
  selectedExtras: string[]
): MotorFormAnswer => ({
  direction: StepDirection.StayHere,
  formValues: { selectedExtras },
  apiResponses: {},
});

export const setPolicyStartDate = (
  policyStartDate: string
): MotorFormAnswer => ({
  direction: StepDirection.StayHere,
  formValues: { policyStartDate },
  apiResponses: {},
});

export const setCoverType = (coverType: MotorCoverType): MotorFormAnswer => ({
  direction: StepDirection.StayHere,
  formValues: { selectedCoverType: coverType },
  apiResponses: {},
});

export const setCoverForDriversUnder25 = (
  coverDriversUnder25: YesNoAnswer
): MotorFormAnswer => ({
  direction: StepDirection.StayHere,
  formValues: { coverForDriversUnder25: coverDriversUnder25 },
  sliceToUpdate: MotorFlowType.QuoteDetails,
  apiResponses: {},
});

export const setPaymentDate = (paymentDate: string): MotorFormAnswer => ({
  direction: StepDirection.StayHere,
  formValues: { paymentDate },
  apiResponses: {},
});

export const removeDriver = (driverIndex: number): MotorMoveFlowAction => ({
  direction: StepDirection.MoveFlow,
  formValues: { driverIndex },
  apiResponses: {},
  targetFlow: MotorMoveFlows.RemoveDriver,
});

export const editDriver = (
  targetStep: MotorStepType,
  driverIndex?: number,
  nameEdit?: boolean
): MotorMoveFlowAction => ({
  direction: StepDirection.MoveFlow,
  formValues: { driverIndex },
  apiResponses: {},
  targetFlow: nameEdit
    ? MotorMoveFlows.EditPolicyDetails
    : MotorMoveFlows.EditDriver,
  targetStep,
  sliceToUpdate: nameEdit ? MotorFlowType.QuoteDetails : undefined,
});

// Records the index for the driver to be updated
export const updateDriver = (driverIndex?: number): MotorFormAnswer => ({
  direction: StepDirection.Next,
  formValues: { driverIndex, editingDriver: true },
  sliceToUpdate: MotorFlowType.QuoteDetails,
  apiResponses: {},
});

export const editReviewDriver = (
  driverIndex?: number
): MotorMoveFlowAction => ({
  direction: StepDirection.MoveFlow,
  formValues: { driverIndex },
  apiResponses: {},
  targetFlow: MotorMoveFlows.EditReviewDriver,
});

export const editQuoteDetails = (
  targetStep: MotorStepType
): MotorMoveFlowAction => ({
  direction: StepDirection.MoveFlow,
  formValues: {},
  apiResponses: {},
  targetFlow: MotorMoveFlows.EditQuoteDetails,
  targetStep,
});

export const editPolicyDetails = (
  targetStep: MotorStepType
): MotorMoveFlowAction => ({
  direction: StepDirection.MoveFlow,
  formValues: {},
  apiResponses: {},
  targetFlow: MotorMoveFlows.EditPolicyDetails,
  targetStep,
});

export const editCoverDetails = (
  targetStep: MotorStepType
): MotorMoveFlowAction => ({
  direction: StepDirection.MoveFlow,
  formValues: {},
  apiResponses: {},
  targetFlow: MotorMoveFlows.EditCoverDetails,
  targetStep,
});

export const apiResponse = (
  apiResponses: Partial<MotorApiResponses>
): MotorFormAnswer => ({
  direction: StepDirection.ApiResponse,
  formValues: {},
  apiResponses,
});

export const setPromoCode = (promoCode: string): MotorFormAnswer => ({
  direction: StepDirection.StayHere,
  formValues: { promoCode },
  apiResponses: {},
});

export const setHasFlybuys = (hasFlybuys: string): MotorFormAnswer => ({
  direction: StepDirection.StayHere,
  formValues: {
    hasFlybuys: hasFlybuys === 'Yes' ? YesNoAnswer.Yes : YesNoAnswer.No,
  },
  apiResponses: {},
});

export const setFlybuysNumber = (flybuysNumber: string): MotorFormAnswer => ({
  direction: StepDirection.StayHere,
  formValues: { flybuysNumber, hasFlybuys: YesNoAnswer.Yes },
  apiResponses: {},
});

export const setCardType = (paymentCardType: CardTypes): MotorFormAnswer => ({
  direction: StepDirection.StayHere,
  formValues: { paymentCardType },
  apiResponses: {},
});

export const setPaymentMethod = (
  paymentMethod: PaymentMethod
): MotorFormAnswer => ({
  direction: StepDirection.StayHere,
  formValues: { paymentMethod },
  apiResponses: {},
});
